/** Error message and detail extraction: a thrown value's words, its cause chain, and its boundary members. */

import { ERROR_MESSAGES } from '@federa/const/error-messages';
import { isBaseError } from '@federa/const/base-error';
import { errorMessageOrNull } from '@federa/const/error-text';
import { HTTP_STATUS } from '@federa/const/http-status';
import { splitLines, withFollowingLine } from '@federa/const/line-splitting';
import { STACK_TRACE_CAPS } from '@federa/const/stack-trace-caps';
import { TYPESCRIPT_SYNTAX_ERROR_CODE } from '@federa/const/working-tree-load-failure-classify';
import { isRecordValue, recordStringOr } from '@federa/const/record-guards';
import { stringsOnly } from '@federa/const/list-coercion';
import { coerceString, nonBlankStringOrNull } from '@federa/const/string-guards';
import type { BaseError } from '@federa/const/base-error';
import type { ErrorResponseShape } from '@federa/types/adapters-response';

/** Standardized error envelope from a caught exception. The failure propagates in-band -- `success: false` plus a `prefix`-ed `getErrorMessage(error)` -- so the response IS the failure record, mirroring `buildAdapterError`'s no-data shape. */
export function buildErrorResponse(
	error: unknown,
	prefix?: string,
	status: number = HTTP_STATUS.INTERNAL_SERVER_ERROR,
): ErrorResponseShape {
	const errorMessage = prefix ? `${prefix}: ${getErrorMessage(error)}` : getErrorMessage(error);
	return { success: false, error: errorMessage, message: errorMessage, status };
}

/** Message-form no-data error envelope for callers whose return contract declares the narrower `data?: Record<string, unknown>`, where `buildAdapterError`'s `data?: unknown` fails assignability. */
export function buildErrorShape(message: string, status: number = HTTP_STATUS.INTERNAL_SERVER_ERROR): ErrorResponseShape {
	return { success: false, error: message, message, status };
}

/** Extract a user-facing message from an unknown error, falling back to `defaultMessage`. The three READING arms are
 *  `errorMessageOrNull` (`@federa/const/error-text`); what this function adds, and all it adds, is the fallback. The
 *  arms are const-homed because `utils/async/backoff-core.ts` needs them and, as a pure utils leaf, may not
 *  value-import this one -- so it carried a byte-parity private copy until they moved. `?? ` and not `||` is
 *  load-bearing: an `Error` whose message is `''` reads as `''` here, exactly as it always has, and does not fall
 *  through to the default. */
export function getErrorMessage(error: unknown, defaultMessage?: string): string {
	return errorMessageOrNull(error) ?? defaultMessage ?? ERROR_MESSAGES.UNKNOWN_ERROR;
}

/** The error's own words for a PERSON to read, or `fallback` when it carries none -- no reading arm matched, or the words
 *  are blank (an `Error('')`), which a surface would otherwise draw as an empty line. {@link getErrorMessage} keeps the
 *  `''` on purpose; a line on screen reads through this, `null` where the caller shows nothing instead. */
export function nonBlankErrorMessage<T extends string | null>(error: unknown, fallback: T): string | T {
	return nonBlankStringOrNull(errorMessageOrNull(error)) ?? fallback;
}

/** The SERVER-SIDE log context for a caught error: its message as `message`. A log line's context only -- a response
 *  payload never carries it (`SEC-ERROR-DETAIL-LEAK-001`); the webhook entries each spelled this object inline. */
export function errorLogContext(error: unknown): { message: string } {
	return { message: getErrorMessage(error) };
}

/** Extract name + message + stack; non-Errors report name `UnknownError`. */
export function getErrorDetails(error: unknown): {
	name: string;
	message: string;
	stack?: string | undefined;
} {
	if (error instanceof Error) return { name: error.name, message: error.message, stack: error.stack };
	return { name: 'UnknownError', message: getErrorMessage(error) };
}

/** Normalize any thrown value into an Error instance; non-Errors get name `NormalizedError`. */
export function normalizeError(error: unknown, defaultMessage?: string): Error {
	if (error instanceof Error) return error;
	const normalized = new Error(getErrorMessage(error, defaultMessage));
	normalized.name = 'NormalizedError';
	return normalized;
}

/** A failed read's error as the one line a pane prints, or null while there is none -- a query's `error` member read
 *  through {@link nonBlankErrorMessage}, so a rejection with no words of its own still says something. */
export function failureMessageOrNull(error: unknown): string | null {
	return error ? nonBlankErrorMessage(error, ERROR_MESSAGES.UNKNOWN_ERROR) : null;
}

export function getErrorStack(error: unknown): string | undefined {
	if (error instanceof Error) return error.stack;
	if (error && typeof error === 'object' && 'stack' in error && typeof error.stack === 'string') return error.stack;
	return undefined;
}

/** A CAUSE AS IT CROSSES A BOUNDARY, JSON-safe: an `Error`'s name and message with its stack and a `BaseError`'s code
 *  where it has them, a thrown string as itself, anything else `Unknown`. The adapter boundary and the flow engine's
 *  continue-record both carry a cause in this one shape, which each had spelled privately. */
export function describeErrorCause(cause: unknown): { name: string; message: string; stack?: string; code?: string } {
	if (!(cause instanceof Error)) return { name: typeof cause === 'string' ? 'String' : 'Unknown', message: getErrorMessage(cause, '') };
	const stack = getErrorStack(cause);
	return {
		name: cause.name,
		message: cause.message,
		...(stack === undefined ? {} : { stack }),
		...(isBaseError(cause) ? { code: cause.code } : {}),
	};
}

/** A BASE ERROR'S TAXONOMY AS RECORD MEMBERS: its `code` always, its `details` and its {@link describeErrorCause}d cause
 *  when it carries them. The adapter error envelope and the flow engine's continue-record both keep exactly these,
 *  and each had spelled the two guarded copies privately. Stack traces are kept: the record crosses the worker boundary
 *  as JSON, and truncating happens at the logging layer. */
export function baseErrorMembers(err: BaseError): {
	readonly code: string;
	readonly details?: BaseError['details'];
	readonly cause?: ReturnType<typeof describeErrorCause>;
} {
	return {
		code: err.code,
		...(err.details === undefined ? {} : { details: err.details }),
		...(err.cause === undefined ? {} : { cause: describeErrorCause(err.cause) }),
	};
}

/** Message plus its stack on a following line, or the bare message when the thrown value carried no stack. The step-error
 *  rendering both flow-engine subflow runners spelled identically before the 2026-08-06 fold; hosted here rather than in a
 *  sibling leaf because its two collaborators are declared in THIS file and a utils module may not value-import a utils
 *  sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`). */
export function errorWithStack(error: unknown): string {
	return withFollowingLine(getErrorMessage(error), getErrorStack(error));
}

/** Truncate an error stack trace to the first `maxLines` LINES, for terse logging. The default reads from the shared
 *  stack-caps leaf, never a bare literal: a literal cap is invisible to every grep for the stack caps it belongs
 *  beside. `STACK_TRACE_CAPS.MAX_CHARS` is the sibling CHARACTER bound and is not interchangeable. */
export function truncateStackTrace(error: unknown, maxLines: number = STACK_TRACE_CAPS.DEFAULT_LINES): string | undefined {
	const stack = getErrorStack(error);
	if (!stack) return undefined;
	const lines = splitLines(stack);
	return lines.length <= maxLines ? stack : lines.slice(0, maxLines).join('\n');
}

/** Appends the per-bullet `unsatisfiedBullets` payload when present and non-empty, so respond-dod-unsatisfied reaches batch-dispatch consumers instead of collapsing to the bare bucket label. The reason literal still leads, keeping downstream prefix-matches working. */
function buildReasonDiagnostic(reason: string, rawBullets: unknown): string {
	if (!Array.isArray(rawBullets) || rawBullets.length === 0) return reason;
	// `stringsOnly` and not `nonEmptyStringsOnly` (`const/list-coercion.ts`): the members are joined into a human
	// diagnostic with `'; '`, so a blank bullet shows up as an empty segment -- visible evidence that a bullet arrived
	// without text. Dropping it would make an unsatisfied bullet disappear from the diagnostic entirely, which is the
	// one thing this payload exists to prevent. The `Array.isArray` guard above STAYS: it also gates the `.length === 0`
	// early return that keeps the bare reason label.
	const bulletTexts = stringsOnly(rawBullets);
	return bulletTexts.length > 0 ? `${reason}: ${bulletTexts.join('; ')}` : reason;
}

/** Extracts an error string from a flow result's nested `body`. Canonical exception keys (error/message) keep precedence; `reason` is the fallback so the gate-reject diagnostic survives instead of collapsing to the caller's 'Flow failed' default. */
function extractFromResultBody(body: Record<string, unknown>): string | null {
	if (typeof body.error === 'string') return body.error;
	if (typeof body.message === 'string') return body.message;
	if (typeof body.reason === 'string') return buildReasonDiagnostic(body.reason, body.unsatisfiedBullets);
	return null;
}

/** Extracts an error string from `result.error` or `result.body.error`, enriching `reason` via buildReasonDiagnostic. `extractFailureReason` in utils/flow-engine/extract-failure-reason.ts has DIFFERENT semantics by design (non-string passthrough, `out.output` fallback), so the same failed envelope can yield different strings per path -- not drift. */
export function extractNestedError(result: unknown): string | null {
	if (!isRecordValue(result)) return null;
	const out = result;
	if (out.body && typeof out.body === 'object') {
		const fromBody = extractFromResultBody(out.body as Record<string, unknown>);
		if (fromBody !== null) return fromBody;
	}
	if (typeof out.error === 'string') return out.error;
	if (typeof out.message === 'string') return out.message;
	if (typeof out.reason === 'string') return out.reason;
	return null;
}

/**
 * The error text a FAILED FlowRunner envelope carries: the PROMOTED top-level `error` first, then whatever
 * {@link extractNestedError} can dig out of `result` for a run whose handler did not promote it. Null when neither
 * exists, so each caller still supplies its OWN canonical fallback -- the two live callers word that differently on
 * purpose (a generic execution-failed message vs one naming the flow), and that difference is not what was duplicated.
 *
 * Typed STRUCTURALLY on the two members it reads, so both the engine's `FlowRunnerResponse` and the tool seam's
 * envelope satisfy it without either package importing the other's shape.
 *
 * @runtime-derived-ssot-helper: delegates the nested half to {@link extractNestedError} in this same leaf rather than
 * re-walking the body, so the two sources cannot disagree about their order of preference.
 */
export function flowEnvelopeError(envelope: { error?: string; result?: unknown }): string | null {
	return envelope.error ?? extractNestedError(envelope.result);
}

/** The error reader the transport-failure classifier (`utils/http/transport-failure-classify.ts`) takes injected,
 *  bundled ONCE here beside its body: that utils leaf may not import this sibling, so each dialler binds it, and both
 *  diallers (the tools fetch transport, the app flow client) bind this one bag. */
export const TRANSPORT_FAILURE_ERROR_READERS = { getErrorMessage } as const;

/** NodeJS SystemError diagnostic fields surfaced inline alongside name+message. A `getErrorMessage`-only logger strips these, leaving operators to guess at errno and target path. */
const SYSTEM_ERROR_FIELDS = ['code', 'errno', 'syscall', 'path'] as const;

function formatSystemErrorFields(error: unknown): string {
	if (!isRecordValue(error)) return '';
	const rec = error;
	const parts: string[] = [];
	for (const field of SYSTEM_ERROR_FIELDS) {
		const value = rec[field];
		if (value === undefined || value === null) continue;
		// `coerceString` (`@federa/const/string-guards`), not the hand-rolled ternary this line carried: the two differ
		// ONLY on a nullish input, and the guard above has already skipped that -- so the SSOT is adoptable here with
		// zero behaviour delta, which is exactly the test `utils/data/record-fields.ts` FAILS (its
		// `coerceFieldToString` stringifies a missing key to `'undefined'` on purpose, where `coerceString` answers `''`).
		parts.push(`${field}=${coerceString(value)}`);
	}
	return parts.length > 0 ? ` (${parts.join(', ')})` : '';
}

function extractStackOriginFromLine(line: string): string {
	// Node's strip-types loader emits the offending URL with `:LINE` and SOMETIMES `:LINE:COL` (the header frame is line-only, e.g. `file:///x.ts:1`), so the column segment is optional.
	const urlMatch = /(file:\/\/\/[^\s)]+?:\d+(?::\d+)?)/.exec(line);
	if (urlMatch?.[1]) return ` (at ${urlMatch[1]})`;
	const pathMatch = /([A-Za-z]:[\\/][^\s)]+?:\d+(?::\d+)?|\/[^\s)]+?:\d+(?::\d+)?)/.exec(line);
	if (pathMatch?.[1]) return ` (at ${pathMatch[1]})`;
	return '';
}

function formatStackOrigin(error: unknown): string {
	if (!isRecordValue(error)) return '';
	const rec = error as { name?: unknown; code?: unknown; stack?: unknown };
	if (!(rec.name === 'SyntaxError' || rec.code === TYPESCRIPT_SYNTAX_ERROR_CODE)) return '';
	const stack = recordStringOr(rec, 'stack', '');
	if (!stack) return '';
	// The file URL can land on the stack header (strip-types loader) OR on a frame line, so scan every non-empty line and return the first extractable origin rather than inspecting only the first.
	for (const line of splitLines(stack)) {
		if (line.trim().length === 0) continue;
		const origin = extractStackOriginFromLine(line);
		if (origin) return origin;
	}
	return '';
}

function formatErrorFrame(error: unknown): string {
	const origin = formatStackOrigin(error);
	if (error instanceof Error) {
		return `${error.name}: ${error.message}${formatSystemErrorFields(error)}${origin}`;
	}
	if (typeof error === 'string') return `String: ${error}`;
	return `Unknown: ${getErrorMessage(error)}${formatSystemErrorFields(error)}${origin}`;
}

/** Formats an error and its full `cause` chain for top-level loggers (uncaughtException handlers, FlowRunner failure summaries, daemon stderr). {@link getErrorMessage} returns `error.message` only, and dropping `.cause` is what masks the underlying syscall errno on wrapped throws. Output: `<ErrorName>: <message> (code=..., errno=..., syscall=..., path=...)` then an indented `caused by:` line per cause; cycle- and depth-guarded, with the root counting as 1 so `depth=5` surfaces the root plus 4 causes. */
export function getErrorChain(error: unknown, depth = 5): string {
	const lines: string[] = [];
	const seen = new Set<unknown>();
	let current: unknown = error;
	let frame = 0;
	while (current !== undefined && current !== null && frame < depth) {
		if (seen.has(current)) {
			lines.push(`${'  '.repeat(frame)}${frame === 0 ? '' : 'caused by: '}<cycle detected>`);
			break;
		}
		seen.add(current);
		const indent = '  '.repeat(frame);
		const prefix = frame === 0 ? '' : 'caused by: ';
		lines.push(`${indent}${prefix}${formatErrorFrame(current)}`);
		const next: unknown = current instanceof Error ? (current as Error & { cause?: unknown }).cause : undefined;
		if (next === undefined) break;
		current = next;
		frame += 1;
	}
	return lines.length > 0 ? lines.join('\n') : formatErrorFrame(error);
}

/**
 * Serialize an ALREADY-EXTRACTED flow-failure reason for an envelope `error` field: `undefined` passes through as
 * "no error", a string reason passes through VERBATIM, and any other shape renders through {@link getErrorChain}.
 *
 * It lives beside `getErrorChain` rather than next to `extractOutputError` (`utils/flow-engine/extract-failure-reason.ts`)
 * for a hard layering reason: `utils/` may value-import only const/types/zod, so a leaf holding BOTH halves is
 * impossible in either direction. Splitting at the already-extracted boundary is what makes ONE shared body legal --
 * this half needs no import at all, and each caller (all non-`utils/`, so both imports are legal there) composes it
 * as `serializeErrorTextOrChain(extractOutputError(x))`.
 *
 * The string pass-through is the load-bearing part. `extractOutputError` returns an OBJECT-shaped reason for a
 * thrown failure but a plain string for a handler-reported one, and passing that string through `getErrorChain`
 * would re-wrap an already-human-readable message as `String: <msg>` -- so the two arms are not interchangeable.
 */
export function serializeErrorTextOrChain(rawError: unknown): string | undefined {
	if (rawError === undefined) return undefined;
	return typeof rawError === 'string' ? rawError : getErrorChain(rawError);
}

/** Word-boundary matched so a substring cannot false-fire. Keep this list TIGHT and add a token only on an observed leak: a false positive shows the fallback where the original message was fine. */
const VENDOR_TOKEN_PATTERNS: readonly RegExp[] = [
	/\bstripe\b/i,
	/\brecaptcha\b/i,
	/\bfirebase\b/i,
	/\btwilio\b/i,
	/\bcloudflare\b/i,
	/\bworker(s)?\b/i,
	/\bwebhook\b/i,
	/** `D1` alone and its error-code spellings (`D1_ERROR`, `D1_TYPE_ERROR`): the underscore is a word character, so
	 *  the bare `\bd1\b` arm never fired on the code, only on prose like `in D1 DB`. Witnessed on the offer modal
	 *  2026-09-24: `Task insert failed: D1_ERROR: Internal error in D1 DB storage caused object to be reset`. */
	/\bd1(?:_[a-z]+)*\b/i,
	/\bkv\b/i,
	/\bvectorize\b/i,
	/\br2\b/i,
	/\bdurable\s*object(s)?\b/i,
	/\bwrangler\b/i,
	/\bopenai\b/i,
	/\banthropic\b/i,
];

/** Marks a stack/infrastructure detail even without a vendor name. */
const INTERNAL_PHRASE_PATTERNS: readonly RegExp[] = [
	/\bsql(ite)?\b/i,
	/\bbinding\b/i,
	/\bunhandled\s+rejection\b/i,
	/\btypeerror\b/i,
	/\breferenceerror\b/i,
	/\bsyntaxerror\b/i,
	/\bECONN[A-Z]+\b/,
	/\bENOTFOUND\b/,
	/\b5\d{2}\b\s+(internal|bad gateway|service unavailable)/i,
	// The storage-layer phrases a wrapped D1 failure carries even when its vendor code is stripped upstream.
	/\binternal error\b/i,
	/\bconstraint failed\b/i,
	/\bno such (?:table|column)\b/i,
	/\b(?:insert|update|upsert|delete) failed\b/i,
	// A flow STEP id reporting its own failure (`offers-validation-buy-transaction-auto-create failed: ...`): three or
	// more kebab segments is the step-id shape, and no authored user sentence is written that way.
	/\b[a-z0-9]+(?:-[a-z0-9]+){2,} failed\b/i,
];

function containsVendorOrInternalToken(message: string): boolean {
	for (const pattern of VENDOR_TOKEN_PATTERNS) if (pattern.test(message)) return true;
	for (const pattern of INTERNAL_PHRASE_PATTERNS) if (pattern.test(message)) return true;
	return false;
}

/** Choke-point for end-user error messages: raw `setError(getErrorMessage(err))` leaks vendor names and infrastructure
 * terminology into the UI, so this substitutes the caller's `fallback`. BaseError messages take the SAME scrub. The
 * FALLBACK is scrubbed too, so a caller who interpolates a raw error into it degrades to a generic sentinel rather
 * than leaking.
 *
 * Homed in this leaf rather than one of its own. It composes {@link nonBlankErrorMessage} and nothing else -- which is
 * the membership test this leaf's own file-size grant records for the error family -- and a separate `utils/` leaf
 * could not value-import that sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`). */
export function userSafeError(err: unknown, fallback: string): string {
	const safeFallback = containsVendorOrInternalToken(fallback) ? ERROR_MESSAGES.GENERIC_RETRY : fallback;
	const candidate = nonBlankErrorMessage(err, safeFallback);
	if (containsVendorOrInternalToken(candidate)) return safeFallback;
	return candidate;
}

/** A failed query's or mutation's error as the user-safe sentence a control shows ({@link userSafeError}), or null
 *  while it has not failed -- the `result.isError ? userSafeError(result.error, ...) : null` each form spelled per
 *  control. The fallback defaults to the generic sentinel {@link userSafeError} itself degrades to. */
export function userSafeFailureOrNull(
	result: { readonly isError: boolean; readonly error: unknown },
	fallback: string = ERROR_MESSAGES.GENERIC_RETRY,
): string | null {
	return result.isError ? userSafeError(result.error, fallback) : null;
}
